'use client'

/**
 * Shared presentational pieces for the issues tables.
 *
 * Both the inline table (issues-list-view) and the virtualized table render
 * the same columns, so the cell visuals live here to stay in sync.
 * Theme-driven only — one primary colour, no per-value accent colours.
 */

import { formatDistanceToNowStrict } from 'date-fns'

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
}

/** Round initials avatar — uniform primary tint, theme-driven. */
export function UserAvatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-[var(--of-primary-10)] font-semibold text-[hsl(var(--primary))]"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      title={name}
    >
      {initials(name)}
    </div>
  )
}

/** Assignee cell — avatar + name, or a muted "Unassigned" placeholder. */
export function AssigneeCell({ name }: { name?: string }) {
  if (!name) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="h-6 w-6 shrink-0 rounded-full border border-dashed border-muted-foreground/40" />
        <span className="text-xs">Unassigned</span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <UserAvatar name={name} />
      <span className="truncate text-sm text-foreground">{name}</span>
    </div>
  )
}

/** Priority cell — primary dot + label. The label carries the urgency. */
export function PriorityTag({ name }: { name?: string }) {
  if (!name) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
      <span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
      {name}
    </span>
  )
}

/** Status cell — small dot + label, no pill. Closed statuses are muted. */
export function StatusTag({ name, isClosed = false }: { name?: string; isClosed?: boolean }) {
  if (!name) return <span className="text-muted-foreground">—</span>
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isClosed ? 'text-muted-foreground' : 'text-foreground'}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${isClosed ? 'bg-muted-foreground/40' : 'bg-[hsl(var(--primary))]'}`} />
      {name}
    </span>
  )
}

/** Short, clickable issue reference derived from the (UUID) id. */
export function shortId(id: string | number): string {
  const s = String(id)
  return s.includes('-') ? s.slice(0, 6) : s
}

/** Compact relative time, e.g. "3d", "5h". Empty string when unknown. */
export function relativeTime(value?: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return formatDistanceToNowStrict(d, { addSuffix: false })
    .replace(' seconds', 's')
    .replace(' second', 's')
    .replace(' minutes', 'm')
    .replace(' minute', 'm')
    .replace(' hours', 'h')
    .replace(' hour', 'h')
    .replace(' days', 'd')
    .replace(' day', 'd')
    .replace(' months', 'mo')
    .replace(' month', 'mo')
    .replace(' years', 'y')
    .replace(' year', 'y')
}
